import React from 'react';
import './App.css';
import { defaultTheme, Provider as ProviderV3, Flex } from '@adobe/react-spectrum';
import 'react-toastify/dist/ReactToastify.css';
import { Provider } from 'react-redux';
import store from './data/redux/store/AppStore';
import { initializeApp } from 'firebase/app';
import { initializeAnalytics, getAnalytics, logEvent } from 'firebase/analytics';
import { hydrate } from './data/redux/slices/appSlice';
import { getUserProfile } from './data/redux/slices/profileSlice';
import AppRouter from './AppRouter';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useSpectrumTrayFix } from './hooks/useSpectrumTrayFix';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';
import { CardInfoDialog } from './components/common/cardInfoDialog/CardInfoDialog';

// Module-level guard so app_load fires exactly once per page load even if
// React re-renders before hydrate() flips isHydrated or StrictMode double-
// mounts in dev. Previous in-render call was inflating the count by ~30%.
let appLoadFired = false;

function App() {
    try {
        if (!store?.getState()?.app?.isHydrated) {
            const firebaseConfig = {
                apiKey: 'AIzaSyBwfiLrvKfB6lBzToMhkZbdjbot2xpBZ1U',
                authDomain: 'commandersalt-a90fc.firebaseapp.com',
                projectId: 'commandersalt-a90fc',
                storageBucket: 'commandersalt-a90fc.appspot.com',
                messagingSenderId: '1021980073373',
                appId: '1:1021980073373:web:e7590afb834b6a0f20a374',
                measurementId: 'G-4ZBGH8CG72',
            };

            try {
                const app = initializeApp(firebaseConfig);
                // debug_mode routes events to Firebase DebugView in real time
                // instead of the standard reporting stream, which has a 24-48h
                // delay and is typically filtered as internal traffic.
                const isLocalhost = window.location.hostname === 'localhost'
                    || window.location.hostname === '127.0.0.1';
                initializeAnalytics(app, {
                    config: {
                        send_page_view: false,
                        ...(isLocalhost && { debug_mode: true }),
                    },
                });
                window.firebaseApp = app;
            } catch (error) {
                console.log(`unable to initialize metrics`, error);
            }

            if (window.location.toString().includes(`/#/`)) {
                window.location = window.location.toString().replace(/\/#\//g, '/');
            }

            try {
                store.dispatch(hydrate());
            } catch (error) {
                console.log(`unable to hydrate app state`, error);
            }
        }

        const auth = useAuth();
        const [hasTriedSignin, setHasTriedSignin] = React.useState(false);

        // Check for new versions every 5 minutes
        const { hasNewVersion } = useVersionCheck(5 * 60 * 1000);

        // Fix Spectrum Tray positioning on mobile
        useSpectrumTrayFix();

        React.useEffect(() => {
            if (appLoadFired) return;
            appLoadFired = true;
            try {
                logEvent(getAnalytics(), 'app_load');
            } catch (e) {
                // analytics not initialized yet
            }
        }, []);

        // automatically sign-in
        React.useEffect(() => {
            if (
                !hasAuthParams() &&
                !auth.isAuthenticated &&
                !auth.activeNavigator &&
                !auth.isLoading &&
                !hasTriedSignin
            ) {
                auth.signinSilent();
                setHasTriedSignin(true);
            }
        }, [auth, hasTriedSignin]);

        // Pull the user profile whenever auth flips to authenticated. The
        // one-shot hydrate() at startup misses this when the token exchange
        // finishes after the initial render (post-login, silent renew).
        React.useEffect(() => {
            if (auth.isAuthenticated) {
                store.dispatch(getUserProfile());
            }
        }, [auth.isAuthenticated]);

        if (auth.isLoading) {
            console.log(`singning in`);
            // return <div>Signing you in/out...</div>;
        }

        if (!auth.isAuthenticated) {
            console.log(`unable to login`);
        }

        return (
            <div>
                <UpdateAvailableBanner show={hasNewVersion} />
                <Provider store={store}>
                    <ProviderV3 theme={defaultTheme} colorScheme="dark">
                        <div className={'appWrapper'}>
                            <AppRouter />
                            <CardInfoDialog />
                        </div>
                    </ProviderV3>
                </Provider>
            </div>
        );
    } catch (error) {
        return (
            <div>
                <span>
                    Unable to render page! Please report error to discord server at https://discord.gg/Yb2CAxcJcf
                </span>
                <div>{JSON.stringify(error, null, 4)}</div>
            </div>
        );
    }
}

export default App;
